import axios from 'axios';

import {
  captureAxiosError,
  createRequestId,
  CustomAxiosError,
  KC_REQUEST_ID_HEADER,
  STATUS_CODE,
} from '@kc-console/core';

import useServiceAppState from './useServiceAppState';

import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  CustomParamsSerializer,
  ParamsSerializerOptions,
  RawAxiosRequestHeaders,
} from 'axios';

type RejectedHandlerOptions = {
  getAggregatedAxiosError?: (error: AxiosError) => any;
  getShouldRedirectUnauthorized?: (error: AxiosError) => boolean;
};

type UseCreateInstanceReqParams = {
  baseURL: string;
  overrideAxiosRequestConfig?: AxiosRequestConfig;
  overrideOnFulfilled?: (response: AxiosResponse) => any;
  paramsSerializer?: ParamsSerializerOptions | CustomParamsSerializer;
  rejectedHandlerOptions?: RejectedHandlerOptions;
  useServiceToken?: boolean;
  /** config-api처럼 x-kc-request-id를 수신·로깅하는 백엔드 호출에만 켭니다. */
  withRequestId?: boolean;
};

const DEFAULT_TIMEOUT = 30 * 1000;

/**
 * - kakaocloud console 서비스 API 연동을 위한 axios instance 설정 시 사용하는 리액트 커스텀 훅
 * @example
 * const { data: serviceMeta } = useServiceMetaQuery();
 * const { endpoints = {} } = serviceMeta || {};
 * const baseURL = serviceMeta.endpoints[endpointKey];
 * const axiosInstance = useCreateInstance({
 *   baseURL,
 *   overrideAxiosRequestConfig: {
 *     timeout: 30 * 1000,
 *     ...extraOptions
 *   },
 *   overrideOnFulfilled: response => response
 * });
 */
const useCreateAxiosInstance = (params: UseCreateInstanceReqParams) => {
  const {
    baseURL,
    overrideAxiosRequestConfig = {},
    overrideOnFulfilled,
    rejectedHandlerOptions = {
      getShouldRedirectUnauthorized: {},
      getAggregatedAxiosError: {},
    },
    useServiceToken = false,
    paramsSerializer,
    withRequestId = false,
  } = params;
  const { headers: overrideHeaders = {} } = overrideAxiosRequestConfig;
  const {
    state: { token, serviceKeyword, displayLanguage },
    setClientAppStatus,
  } = useServiceAppState();

  const headers: RawAxiosRequestHeaders = {
    'x-auth-token': token,
    'content-type': 'application/json; charset=utf-8',
    accept: 'application/json',
    'Accept-Language': displayLanguage,
  };
  if (useServiceToken) delete headers['x-auth-token'];

  const axiosInstance = axios.create({
    timeout: DEFAULT_TIMEOUT,
    baseURL,
    withCredentials: serviceKeyword === 'iam',
    paramsSerializer,
    ...overrideAxiosRequestConfig,
    headers: {
      ...headers,
      ...overrideHeaders,
    },
  });

  const { getShouldRedirectUnauthorized, getAggregatedAxiosError } = rejectedHandlerOptions;

  /**
   * kakaocloud console onFulfilled 공통 로직
   */
  const onFulfilled =
    overrideOnFulfilled ||
    ((response: AxiosResponse) => {
      return response.data ?? {};
    });

  /**
   * kakaocloud console onRejected 공통 로직
   */
  const onRejected = (error: AxiosError) => {
    const customAxiosError = new CustomAxiosError(error);

    const { status } = customAxiosError.response || {};

    const shouldRedirectUnauthorized =
      typeof getShouldRedirectUnauthorized === 'function'
        ? getShouldRedirectUnauthorized(customAxiosError)
        : status === STATUS_CODE.UNAUTHORIZED;

    if (shouldRedirectUnauthorized) {
      setClientAppStatus(STATUS_CODE.UNAUTHORIZED_EXPIRED_TOKEN);
    } else {
      captureAxiosError(customAxiosError);
    }

    const refinedError =
      typeof getAggregatedAxiosError === 'function'
        ? getAggregatedAxiosError(customAxiosError)
        : customAxiosError;

    return Promise.reject(refinedError);
  };

  if (withRequestId) {
    axiosInstance.interceptors.request.use(config => {
      config.headers.set(KC_REQUEST_ID_HEADER, createRequestId());
      return config;
    });
  }

  axiosInstance.interceptors.response.use(onFulfilled, onRejected);

  return axiosInstance;
};

export default useCreateAxiosInstance;

export type { UseCreateInstanceReqParams, RejectedHandlerOptions };
